import { http } from '@/core/api'
import type { RecruitmentApplication } from '@/shared/types'
import { resolveExcelHeavyBlobExport } from '@/shared/utils/excel-heavy-operation'

const APPLICATIONS = '/applications'

export type ApplicationInterview = {
  id: number
  application_id: number
  status: string | null
  result: string | null
  scheduled_at: string | null
  attended_at: string | null
  fail_reason: string | null
  note: string | null
  pickup_point: string | null
  expected_start_date?: string | null
  /** True when only the interview date is known (clock stored as 00:00). */
  time_tba?: boolean
}

export type ApplicationPipelineCounts = {
  registered: number
  interviewed: number
  passed: number
  hired: number
  awaiting_pickup: number
  awaiting_result: number
}

export interface ApplicationFilters {
  status?: string
  worker_id?: number
  client_id?: number
  /** Comma-separated or repeated query — multi enterprise scope. */
  client_ids?: string
  client_site_id?: number
  factory_id?: number
  /** Comma-separated multi factory / site scope. */
  client_site_ids?: string
  factory_ids?: string
  job_order_id?: number
  /** Comma-separated multi job-order scope. */
  job_order_ids?: string
  recruiter_id?: number
  /** Recruiter team (recruitment_staff_profiles.team_id). */
  team_id?: number
  /** Comma-separated multi recruitment team scope. */
  team_ids?: string
  /** Comma-separated internal departments without teams (e.g. onsite). */
  department_codes?: string
  /** JO resident onsite staff (commercial_job_orders.bound_onsite_employee_id). */
  bound_onsite_employee_id?: number
  application_id?: number
  source?: string
  search?: string
  applied_from?: string
  applied_to?: string
  interview_from?: string
  interview_to?: string
  interview_status?: string
  interview_result?: string
  factory_confirm?: string
  /** Comma-separated partner API vendor codes (wevn, luxshare, …). */
  partner_api_vendors?: string
  partner_api_vendor?: string
  view?: 'schedule' | 'results' | 'no_show'
  touchpoint_id?: number
  traffic_source_id?: number
  /** Comma-separated multi traffic source scope. */
  traffic_source_ids?: string
  traffic_channel_id?: number
  /** Comma-separated multi traffic channel scope. */
  traffic_channel_ids?: string
  channel_supplier_id?: number
  /** Comma-separated multi supplier channel scope. */
  channel_supplier_ids?: string
  platform?: string
  /** Comma-separated multi approach platform codes. */
  platforms?: string
  ingestion_method?: string
  /** supplier = NCC; internal = nhóm nội bộ (QR NV / nhập tay). */
  submit_origin?: 'supplier' | 'internal' | 'portal' | ''
  /** UniApp interview tabs need this; recruitment-web does not. Default omitted = server default. */
  include_view_counts?: boolean
  /** Set false on interview sub-pages that hide pipeline KPI cards. */
  include_pipeline_counts?: boolean
  /**
   * Bust server list meta cache (total + pipeline). Used after mutations / reload.
   */
  refresh_meta?: boolean
  /**
   * @deprecated Prefer server 10s meta cache. Still accepted as an override.
   */
  known_total?: number
  page?: number
  per_page?: number
}

export type ApplicationListResponse = {
  data: RecruitmentApplication[]
  meta?: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
  pipeline_counts?: ApplicationPipelineCounts
}

export const applicationsApi = {
  async list(filters: ApplicationFilters = {}): Promise<ApplicationListResponse> {
    const { data } = await http.get<ApplicationListResponse>(APPLICATIONS, {
      params: {
        ...filters,
        // Web list: skip UniApp view tab counts; pipeline only when page explicitly asks (KPI strip).
        include_view_counts: filters.include_view_counts ?? false,
        include_pipeline_counts: filters.include_pipeline_counts ?? false,
      },
    })
    return data
  },

  async export(filters: ApplicationFilters = {}) {
    const res = await http.get<Blob>(`${APPLICATIONS}/export`, {
      params: { format: 'xlsx', ...filters },
      responseType: 'blob',
      timeout: 300_000,
      validateStatus: (status) => status === 200 || status === 202,
    })
    return resolveExcelHeavyBlobExport(res, { timeoutMs: 300_000 })
  },

  async get(id: number) {
    const { data } = await http.get(`${APPLICATIONS}/${id}`)
    return data as { data: RecruitmentApplication }
  },

  async updateStatus(id: number, payload: { status: string; note?: string }) {
    const { data } = await http.patch(`${APPLICATIONS}/${id}/status`, payload)
    return data as { data: RecruitmentApplication }
  },

  async listFollowUps(id: number, perPage = 50) {
    const { data } = await http.get(`/recruitment/leads/${id}/follow-ups`, {
      params: { per_page: perPage },
    })
    return data as { data: import('@/features/leads/api/lead-library.api').LeadFollowUpEntry[] }
  },

  async listInterviews(id: number) {
    const { data } = await http.get(`${APPLICATIONS}/${id}/interviews`)
    return data as { data: ApplicationInterview[] }
  },

  async siteTransferTargets(id: number) {
    const { data } = await http.get(`${APPLICATIONS}/${id}/site-transfer-targets`)
    return data as {
      data: {
        can_transfer: boolean
        reason: string | null
        current_site: { id: number; name: string; code: string | null } | null
        sites: Array<{
          id: number
          name: string
          code: string | null
          job_orders: Array<{ id: number; code: string | null; title: string | null }>
        }>
      }
    }
  },

  async transferSite(
    id: number,
    payload: { client_site_id: number; job_order_id?: number },
  ) {
    const { data } = await http.post(`${APPLICATIONS}/${id}/site-transfer`, payload)
    return data as { data: RecruitmentApplication; message?: string }
  },

  async createInterview(
    applicationId: number,
    payload: {
      scheduled_at: string
      client_id?: number
      job_order_id?: number
      pickup_point?: string
      note?: string
    },
  ) {
    const { data } = await http.post(`${APPLICATIONS}/${applicationId}/interviews`, payload)
    return data as { data: ApplicationInterview; message?: string }
  },

  async updateInterview(
    interviewId: number,
    payload: {
      scheduled_at?: string
      status?: 'attended' | 'cancelled' | 'no_show'
      result?: 'pass' | 'fail' | 'refused'
      attended_at?: string
      fail_reason?: string
      fail_reason_codes?: string[]
      start_date?: string
      pickup_point?: string
      note?: string
    },
  ) {
    const { data } = await http.patch(`/interviews/${interviewId}`, payload)
    return data as { data: ApplicationInterview; message?: string }
  },

  async listActivities(id: number, perPage = 20) {
    const { data } = await http.get(`${APPLICATIONS}/${id}/activities`, {
      params: { per_page: perPage },
    })
    return data as { data: import('@/shared/types').ActivityLogEntry[]; meta?: Record<string, unknown> }
  },

  async addNote(id: number, payload: { type: string; content: string; outcome?: string }) {
    const { data } = await http.post(`${APPLICATIONS}/${id}/notes`, payload)
    return data
  },

  async revealIdentity(applicationId: number) {
    const { data } = await http.get<{
      data?: { identity_number?: string }
      identity_number?: string
    }>(`${APPLICATIONS}/${applicationId}/identity`)
    const value = data.data?.identity_number ?? data.identity_number ?? ''
    if (!value.trim()) throw new Error('Identity unavailable')
    return value.trim()
  },
}
